import React from "react"
import Layout from "../../components/layout"
import SubNavigation from "../../components/sub-navigation"
import Hero from "../../components/hero"
import Module from "../../components/module"
import MemberVoices from "../../components/member-voices"
import ReadNext from "../../components/read-next"
import GlobalGovernmentDebtChange from "../../images/spotlights/debt-dynamics/debt-dynamics-chart.inline.svg"
import { AppendixWaypoint, Appendix } from "../../components/appendix"

const PAGESLUG = "debt-dynamics"

export default function Economics() {
  return (
    <Layout>
      <SubNavigation id="A Crisis Like No Other" current={PAGESLUG} />
      <Hero image="spotlights/debt-dynamics/hero.jpg" />
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--medium">
            <h1 className="page__title--opener">
              Low interest rates and high debt will shape the years ahead
            </h1>
            <h2 className="page__title">Debt Dynamics</h2>
            <p>
              Low interest rates for over a decade have led to a buildup of
              global financial risks and historically high levels of government
              and private debt in most countries. These debt vulnerabilities
              have significantly increased with the pandemic and the Great
              Lockdown, which has led to large increases in debt and deficits
              beyond those recorded during the global financial crisis.
            </p>
            <p>
              As countries fight the pandemic, they have committed to spend
              whatever it takes to save lives, protect people from losing jobs
              and incomes, and spare companies from bankruptcies, while
              supporting a recovery.
            </p>
            <p>
              Low interest rates make borrowers more vulnerable if interest
              rates rise, and they erode bank profits, which hampers banks’
              ability to lend money to businesses so they can grow.
            </p>
          </div>
          <div className="container container--large container--padding-v-none">
            <Module
              title="Fast-Increasing Debt and Deficits"
              subtitle="Covid-19 and its economic impact will increase fiscal deficits and public debt ratios across countries given higher spending and plunging revenues"
              label="Contributions to the global government debt change 2007–20, % of GDP"
              content={
                <div className="module__content__inner">
                  <figure
                    className="scaling-svg-container"
                    style={{ paddingBottom: "42%" }}
                    aria-label="Chart of contributions to the global government debt change from 2007 to 2020, as a percentage of GDP. From 2007 to 2009 the global government debt change increased sharply from approximately negative 3 percent to over 10 percent. Between 2009 and 2019, it yielded between approximately negative 1 percent to 3 percent, with a positive debt change for most of those years. In 2020, the global government debt change rose sharply to approximately 13 percent."
                  >
                    <GlobalGovernmentDebtChange />
                  </figure>
                  <p className="title--micro title--uppercase title--light">
                    Source: IMF, <em>World Economic Outlook</em> Database.
                    <br />
                    Note: Refers to calendar years.
                  </p>
                </div>
              }
            />
          </div>
          <div className="container container--medium">
            <p>
              The pandemic hit many vulnerable low-income countries hard: 50
              percent of these countries are at high risk of debt distress.
              Economic shocks like the spread of a global virus can stall their
              economies and reverse financing flows, which further complicates
              their ability to manage their debt.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={3} />
            <p>
              The IMF, along with other partner institutions, has worked with
              low-income countries to help strengthen their debt management and
              transparency practices. This includes providing technical support
              as countries develop and publish debt management strategies and
              debt reports.
            </p>
          </div>
          <div className="container container--medium container--padding-v-none">
            <MemberVoices id="debt-dynamics" />
          </div>
          <div className="container container--medium">
            <p>
              Also, given the financing needs to achieve the Sustainable
              Development Goals, the IMF and the World Bank (under the aegis of
              the Group of 20) developed operational guidelines for sustainable
              lending practices. Furthermore, the IMF and the World Bank
              conducted a comprehensive assessment of the evolution of debt
              vulnerabilities in lower-income economies.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={2} />
            <h3 className="page__quote">
              <em className="theme-color--light-blue">
                Supporting the recovery with fiscal measures
              </em>{" "}
              while managing higher government debt levels is a delicate
              balancing act.
            </h3>
            <p>
              Supporting the recovery with fiscal measures while managing higher
              government debt levels is a delicate balancing act. Low interest
              rates will make it easier for some countries to carry higher debt.
              Nevertheless, countries need to address the medical emergency,
              provide lifelines to people and firms, and support the economic
              rebound while ensuring long-term debt sustainability.
            </p>
          </div>
        </div>
      </section>
      <Appendix page={PAGESLUG} />
      <ReadNext sectionID="spotlights" pageID="climate-change" />
    </Layout>
  )
}
